import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchPatientDetail } from "../services/api";
import { updatePatient } from "../services/api";
import styled from "styled-components";
// Removed Layout import
import Card from "../components/Card";
import PageHeader from "../components/PageHeader";
import DataTable from "../components/DataTable";
import SurveyCard from "../components/SurveyCard";
import {
  formatDateInEastern,
  formatDateTimeInEastern
} from "../utils/formatters";

const PatientContainer = styled.div``;

const PatientSummary = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
  gap: 1rem;
`;

const PatientBadge = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 0.3rem 0.8rem;
  background-color: ${(props) => {
    if (props.status === "True") return "var(--success-bg)";
    if (props.status === "Failed") return "var(--error-bg)";
    return "var(--gray-100)";
  }};
  color: ${(props) => {
    if (props.status === "True") return "var(--success-color)";
    if (props.status === "Failed") return "var(--error-color)";
    return "var(--gray-700)";
  }};
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 500;

  &::before {
    content: "";
    display: inline-block;
    width: 0.5rem;
    height: 0.5rem;
    margin-right: 0.5rem;
    border-radius: 50%;
    background-color: ${(props) => {
    if (props.status === "True") return "var(--success)";
    if (props.status === "Failed") return "var(--error)";
    return "var(--gray-400)";
  }};
  }
`;

const TabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid var(--gray-200);
  margin-bottom: 1.5rem;
`;

const Tab = styled.button`
  padding: 0.75rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${(props) =>
    props.active ? "var(--primary-color)" : "var(--gray-500)"};
  border: none;
  background: none;
  border-bottom: 2px solid
    ${(props) => (props.active ? "var(--primary-color)" : "transparent")};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: ${(props) => !props.active && "var(--gray-700)"};
  }
`;

const PatientInfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InfoSection = styled.div`
  margin-bottom: 1.5rem;
`;
const Input = styled.input`
  width: 100%;
  padding: 0.5rem;
  outline: none;
  border-radius: 0.375rem;
  background-color: #1e1e2e; 
  box-shadow: none; 
  color: white;
  border: 1px solid ${props => props.hasError ? "var(--error)" : "transparent"};

  &:focus {
    border-color: ${props => props.hasError ? "var(--error)" : "var(--primary-color)"};
  }
`;

const ErrorText = styled.div`
  color: var(--error);
  font-size: 0.75rem;
  margin-top: 0.25rem;
`;

const BaseButton = styled.button`
  padding: 0.5rem 0.75rem;
  border-radius: 0.375rem;
  font-weight: bold;
  cursor: pointer;
  border: none;
  transition: background-color 0.2s;
`;

const SaveButton = styled(BaseButton)`
  background-color: #16a34a;
  color: white;

  &:hover {
    background-color: #15803d;
  }
`;

const CancelButton = styled(BaseButton)`
  background-color: #d1d5db;
  color: #1f2937;

  &:hover {
    background-color: #9ca3af;
  }
`;

const EditButton = styled(BaseButton)`
  background-color: #3b82f6;
  color: white;

  &:hover {
    background-color: #2563eb;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: var(--primary-color);
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--gray-200);
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
`;

const InfoField = styled.div``;

const FieldLabel = styled.div`
  font-size: 0.875rem;
  color: var(--gray-500);
  margin-bottom: 0.25rem;
`;

const RequiredAsterisk = styled.span`
  color: var(--error);
  font-weight: bold;
`;

const FieldValue = styled.div`
  font-size: 1rem;
  color: var(--gray-800);
`;

const AddressCard = styled.div`
  background-color: var(--gray-50);
  border: 1px solid var(--gray-200);
  border-radius: 0.5rem;
  padding: 1rem;
  margin-bottom: 1rem;
`;

const AddressHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const AddressType = styled.span`
  font-weight: 600;
  color: var(--gray-700);
`;

const AddressDetails = styled.div`
  color: var(--gray-600);
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  border-radius: 0.25rem;
  background-color: ${(props) =>
    props.type === "success"
      ? "var(--success-bg)"
      : props.type === "warning"
        ? "var(--warning-bg)"
        : "var(--gray-100)"};
  color: ${(props) =>
    props.type === "success"
      ? "var(--success-color)"
      : props.type === "warning"
        ? "var(--warning-color)"
        : "var(--gray-700)"};
`;

const ActionButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background-color: var(--primary-color);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--primary-dark);
  }

  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const SectionHeader = styled.div`
  margin-bottom: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SurveysSectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: var(--text-primary);
`;

const SectionCount = styled.div`
  color: var(--gray-500);
  font-size: 0.875rem;
`;

const SurveysContainer = styled.div`
  margin-bottom: 1.5rem;
`;

const EmptyStateContainer = styled.div`
  padding: 2rem;
  text-align: center;
  color: var(--gray-500);
`;

const EmptyStateIcon = styled.svg`
  margin: 0 auto 1rem;
`;

const EmptyStateTitle = styled.p`
  font-size: 1rem;
  margin-bottom: 0.5rem;
  color: var(--text-primary);
`;

const EmptyStateMessage = styled.p`
  font-size: 0.875rem;
  color: var(--text-secondary);
`;

// New Skeleton Components
const SkeletonLine = styled.div`
  height: ${(props) => props.height || "1rem"};
  width: ${(props) => props.width || "100%"};
  background: linear-gradient(
    90deg,
    var(--gray-100) 0%,
    var(--gray-200) 50%,
    var(--gray-100) 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
  margin-bottom: ${(props) => props.mb || "0.5rem"};

  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const SkeletonCircle = styled.div`
  height: ${(props) => props.size || "2rem"};
  width: ${(props) => props.size || "2rem"};
  border-radius: 50%;
  background: linear-gradient(
    90deg,
    var(--gray-100) 0%,
    var(--gray-200) 50%,
    var(--gray-100) 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  margin-bottom: ${(props) => props.mb || "0"};
`;

const SkeletonContainer = styled.div`
  padding: ${(props) => props.padding || "1rem"};
`;

const SkeletonPatientSummary = () => (
  <PatientSummary>
    <div style={{ flex: 1 }}>
      <SkeletonLine height="1.75rem" width="60%" mb="0.75rem" />
      <SkeletonLine height="1rem" width="40%" />
    </div>
    <SkeletonLine height="1.5rem" width="130px" />
  </PatientSummary>
);

const SkeletonTabs = () => (
  <TabContainer>
    <SkeletonLine height="2rem" width="100px" mb="0" />
    <SkeletonLine
      height="2rem"
      width="100px"
      mb="0"
      style={{ marginLeft: "1rem" }}
    />
    <SkeletonLine
      height="2rem"
      width="100px"
      mb="0"
      style={{ marginLeft: "1rem" }}
    />
  </TabContainer>
);

const SkeletonInfoField = () => (
  <InfoField>
    <SkeletonLine height="0.875rem" width="50%" mb="0.5rem" />
    <SkeletonLine height="1rem" width="80%" />
  </InfoField>
);

const SkeletonCard = ({ title, children, className }) => (
  <Card title={title} className={className}>
    <SkeletonContainer>{children}</SkeletonContainer>
  </Card>
);

const SkeletonPatientDetail = () => (
  <PatientContainer>
    <SkeletonPatientSummary />
    <SkeletonTabs />

    <PatientInfoGrid>
      <div>
        <SkeletonCard title="Patient Information">
          <InfoSection>
            <SectionTitle>Contact Information</SectionTitle>
            <FieldGrid>
              <SkeletonInfoField />
              <SkeletonInfoField />
              <SkeletonInfoField />
            </FieldGrid>
          </InfoSection>

          <InfoSection>
            <SectionTitle>Personal Information</SectionTitle>
            <FieldGrid>
              <SkeletonInfoField />
              <SkeletonInfoField />
            </FieldGrid>
          </InfoSection>

          <InfoSection>
            <SectionTitle>Medical Information</SectionTitle>
            <FieldGrid>
              <SkeletonInfoField />
              <SkeletonInfoField />
            </FieldGrid>
          </InfoSection>
        </SkeletonCard>

        <SkeletonCard title="Addresses" className="mt-6">
          <SkeletonLine height="5rem" mb="1rem" />
          <SkeletonLine height="5rem" />
        </SkeletonCard>
      </div>

      <div>
        <SkeletonCard title="Recent Activity">
          <SkeletonLine height="1.2rem" width="40%" mb="1rem" />
          <SkeletonInfoField />
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "1rem",
              marginTop: "1rem"
            }}
          >
            <SkeletonInfoField />
            <SkeletonInfoField />
            <SkeletonInfoField />
            <SkeletonInfoField />
          </div>
          <SkeletonLine
            height="2.5rem"
            width="150px"
            style={{ marginTop: "1.5rem" }}
          />
        </SkeletonCard>

        <SkeletonCard title="Orders Summary" className="mt-6">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "1rem"
            }}
          >
            <SkeletonInfoField />
            <SkeletonInfoField />
          </div>
          <SkeletonLine
            height="1rem"
            width="120px"
            style={{ marginTop: "1rem" }}
          />
        </SkeletonCard>
      </div>
    </PatientInfoGrid>
  </PatientContainer>
);

const PatientDetailPage = () => {
  const { patientId } = useParams();
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [isEditing, setIsEditing] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    date_of_birth: ""
  });

  const isValidPhone = (phone) => {
    if (!phone || phone.trim() === '') return false;
    let digits = phone.replace(/\D/g, '');
    if (digits.startsWith('1') && digits.length === 11) {
      digits = digits.substring(1);
    }
    return digits.length === 10;
  };

  const getPhoneDigits = (phone) => {
    if (!phone) return '';
    let digits = phone.replace(/\D/g, '');
    if (digits.startsWith('1') && digits.length === 11) {
      digits = digits.substring(1);
    }
    return digits;
  };

  const isValidEmail = (email) => {
    if (!email || email.trim() === '') return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e) => {
    const inputValue = e.target.value;
    setForm({ ...form, email: inputValue });

    if (inputValue.trim() === '') {
      setEmailError('Email is required');
    } else if (!isValidEmail(inputValue)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };

  const handlePhoneChange = (e) => {
    const inputValue = e.target.value;
    setForm({ ...form, phone: inputValue });

    if (inputValue.trim() === '') {
      setPhoneError('Phone is required');
    } else if (!isValidPhone(inputValue)) {
      setPhoneError('Phone must be 10 digits');
    } else {
      setPhoneError('');
    }
  };
  useEffect(() => {
    if (patient) {
      let displayPhone = patient.phone || "";
      if (displayPhone.startsWith('+1')) {
        displayPhone = displayPhone.substring(2);
      }

      setForm({
        first_name: patient.first_name || "",
        last_name: patient.last_name || "",
        email: patient.email || "",
        phone: displayPhone,
        date_of_birth: patient.date_of_birth || ""
      });
      setPhoneError("");
      setEmailError("");
    }
  }, [patient]);
  const handleUpdate = async () => {
    try {
      let hasError = false;

      if (!form.email || form.email.trim() === '') {
        setEmailError('Email cannot be empty');
        hasError = true;
      }

      if (!form.phone || form.phone.trim() === '') {
        setPhoneError('Phone cannot be empty');
        hasError = true;
      } else if (!isValidPhone(form.phone)) {
        setPhoneError('Phone must be 10 digits');
        hasError = true;
      }

      if (hasError) {
        setError('Please fix the errors before saving');
        return;
      }
      const dataToSend = { ...form };
      if (dataToSend.phone) {
        dataToSend.phone = getPhoneDigits(dataToSend.phone);
      }

      const updatedData = await updatePatient(patientId, dataToSend);
      setPatient(updatedData);
      setIsEditing(false);
      setPhoneError('');
      setEmailError('');
      setError(null);
      setSuccessMsg('Patient info updated successfully');
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.response?.data?.email?.[0] || 'Update failed';
      if (errorMsg.toLowerCase().includes('email')) {
        setEmailError(errorMsg);
      }
      setError(errorMsg);
    }
  };

  useEffect(() => {
    const loadPatientDetails = async () => {
      try {
        setLoading(true);
        console.log("Fetching patient details for ID:", patientId);
        const data = await fetchPatientDetail(patientId);
        console.log("Received patient data:", data);
        setPatient(data);
        setError(null);
      } catch (err) {
        setError("Failed to load patient details");
        console.error("Error fetching patient:", err);
      } finally {
        setLoading(false);
      }
    };

    loadPatientDetails();
  }, [patientId]);

  if (loading) {
    // Return skeleton loader instead of simple loading text
    return <SkeletonPatientDetail />;
  }

  if (!patient) {
    // Removed Layout wrapper
    return (
      <div className="text-center py-8 text-red-600">
        <div>Error: {error || "Patient not found"}</div>
        <Link
          to="/patients"
          className="text-blue-500 hover:underline mt-4 inline-block"
        >
          Return to Patients List
        </Link>
      </div>
    );
  }

  // Get patient's name, handling empty values
  const patientName =
    `${patient.first_name || ""} ${patient.last_name || ""}`.trim() ||
    "Unnamed Patient";

  // Format the date for display
  const formatDate = (dateString) => {
    if (!dateString) return "Not available";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  };

  // Format date with time
  const formatDateTime = (dateString) => {
    if (!dateString) return "Not available";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  // Format address for display
  const formatAddress = (address) => {
    if (!address) return "No address available";
    const { address1, address2, city, state, zipcode } = address;
    let formattedAddress = address1;
    if (address2) formattedAddress += `, ${address2}`;
    if (city) formattedAddress += `, ${city}`;
    if (state) formattedAddress += `, ${state.state_abbreviation}`;
    if (zipcode) formattedAddress += ` ${zipcode}`;
    return formattedAddress;
  };

  // Get most recent survey
  const latestSurvey =
    patient.surveys && patient.surveys.length > 0
      ? patient.surveys.sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      )[0]
      : null;

  return (
    <PatientContainer>
      <PatientSummary>
        <PageHeader
          title={patientName}
          subtitle={`Patient ID: ${patient.patient_id}`}
          backLink="/patients"
          backText="Back to Patients"
        />
        <PatientBadge status={patient.prognosis_match}>
          {patient.prognosis_match === "True"
            ? "Prognocis Match"
            : patient.prognosis_match === "Failed"
              ? "Prognocis API Failed"
              : "No Prognocis Match"}
        </PatientBadge>
      </PatientSummary>

      <TabContainer>
        <Tab
          active={activeTab === "overview"}
          onClick={() => setActiveTab("overview")}
        >
          Overview
        </Tab>
        <Tab
          active={activeTab === "surveys"}
          onClick={() => setActiveTab("surveys")}
        >
          Surveys ({patient.surveys?.length || 0})
        </Tab>
        <Tab
          active={activeTab === "orders"}
          onClick={() => setActiveTab("orders")}
        >
          Orders ({patient.orders?.length || 0})
        </Tab>
      </TabContainer>

      {activeTab === "overview" && (
        <PatientInfoGrid>
          <div>
            <Card title="Patient Information"
              action={
                <EditButton onClick={() => setIsEditing(true)}>
                  Edit
                </EditButton>
              }
            >
              {isEditing && (
                <div className="mb-4">
                  <InfoSection>
                    <SectionTitle>Edit Contact Information</SectionTitle>
                    <FieldGrid>
                      <InfoField>
                        <FieldLabel>First Name</FieldLabel>
                        <Input
                          type="text"
                          value={form.first_name}
                          onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                        />
                      </InfoField>
                      <InfoField>
                        <FieldLabel>Last Name</FieldLabel>
                        <Input
                          type="text"
                          value={form.last_name}
                          onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                        />
                      </InfoField>
                      <InfoField>
                        <FieldLabel>Email <RequiredAsterisk>*</RequiredAsterisk></FieldLabel>
                        <Input
                          type="email"
                          value={form.email}
                          onChange={handleEmailChange}
                          hasError={!!emailError}

                        />
                        {emailError && <ErrorText>{emailError}</ErrorText>}
                      </InfoField>
                      <InfoField>
                        <FieldLabel>Phone <RequiredAsterisk>*</RequiredAsterisk></FieldLabel>
                        <Input
                          type="tel"
                          value={form.phone}
                          onChange={handlePhoneChange}
                          hasError={!!phoneError}

                        />
                        {phoneError && <ErrorText>{phoneError}</ErrorText>}
                      </InfoField>
                      <InfoField>
                        <FieldLabel>Date of Birth</FieldLabel>
                        <Input
                          type="date"
                          value={form.date_of_birth || ""}
                          onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
                        />
                      </InfoField>
                    </FieldGrid>
                  </InfoSection>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "flex-end",
                      gap: "12px",
                      marginTop: "10px"
                    }}
                  >
                    <SaveButton onClick={handleUpdate}>
                      Save
                    </SaveButton>
                    <CancelButton onClick={() => setIsEditing(false)}>
                      Cancel
                    </CancelButton>
                  </div>

                </div>
              )}
              <InfoSection>
                <SectionTitle>Contact Information</SectionTitle>
                <FieldGrid>
                  <InfoField>
                    <FieldLabel>Full Name</FieldLabel>
                    <FieldValue>{patientName}</FieldValue>
                  </InfoField>
                  <InfoField>
                    <FieldLabel>Email</FieldLabel>
                    <FieldValue>{patient.email || "Not provided"}</FieldValue>
                  </InfoField>
                  <InfoField>
                    <FieldLabel>Phone</FieldLabel>
                    <FieldValue>{patient.phone || "Not provided"}</FieldValue>
                  </InfoField>
                </FieldGrid>
              </InfoSection>

              <InfoSection>
                <SectionTitle>Personal Information</SectionTitle>
                <FieldGrid>
                  <InfoField>
                    <FieldLabel>Date of Birth</FieldLabel>
                    <FieldValue>{formatDate(patient.date_of_birth)}</FieldValue>
                  </InfoField>
                  <InfoField>
                    <FieldLabel>Gender</FieldLabel>
                    <FieldValue>{patient.gender || "Not specified"}</FieldValue>
                  </InfoField>
                </FieldGrid>
              </InfoSection>

              <InfoSection>
                <SectionTitle>Medical Information</SectionTitle>
                <FieldGrid>
                  <InfoField>
                    <FieldLabel>Allergies</FieldLabel>
                    <FieldValue>
                      {patient.allergies || "None reported"}
                    </FieldValue>
                  </InfoField>
                  <InfoField>
                    <FieldLabel>Notes</FieldLabel>
                    <FieldValue>{patient.notes || "No notes"}</FieldValue>
                  </InfoField>
                </FieldGrid>
              </InfoSection>
            </Card>

            <Card title="Addresses">
              {patient.addresses && patient.addresses.length > 0 ? (
                patient.addresses.map((address, index) => (
                  <AddressCard key={address.address_id}>
                    <AddressHeader>
                      <AddressType>Address {index + 1}</AddressType>
                    </AddressHeader>
                    <AddressDetails>
                      <div>{address.address1}</div>
                      {address.address2 && <div>{address.address2}</div>}
                      <div>
                        {address.city}, {address.state?.state_abbreviation}{" "}
                        {address.zipcode}
                      </div>
                    </AddressDetails>
                  </AddressCard>
                ))
              ) : (
                <p>No addresses on file.</p>
              )}
            </Card>
          </div>

          <div>
            <Card title="Recent Activity">
              {latestSurvey ? (
                <div>
                  <SectionTitle>Latest Survey</SectionTitle>
                  <InfoField>
                    <FieldLabel>Date</FieldLabel>
                    <FieldValue>
                      {formatDateTimeInEastern(latestSurvey.created_at)}
                    </FieldValue>
                  </InfoField>

                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <InfoField>
                      <FieldLabel>Current Medicines</FieldLabel>
                      <FieldValue>
                        {latestSurvey.current_medicines || "Not specified"}
                      </FieldValue>
                    </InfoField>
                    <InfoField>
                      <FieldLabel>Current Dose</FieldLabel>
                      <FieldValue>
                        {latestSurvey.current_dose || "Not specified"}
                      </FieldValue>
                    </InfoField>
                    <InfoField>
                      <FieldLabel>Effectiveness</FieldLabel>
                      <FieldValue>
                        {latestSurvey.medicines_effectiveness || "Not rated"}/10
                      </FieldValue>
                    </InfoField>
                    <InfoField>
                      <FieldLabel>Weight Loss Goal</FieldLabel>
                      <FieldValue>
                        {latestSurvey.weight_loss_goal || "Not specified"}
                      </FieldValue>
                    </InfoField>
                  </div>

                  <div className="mt-4">
                    <Link
                      to={`/surveys?search=${patientName}`}
                      className="text-primary-color font-medium hover:underline"
                    >
                      View all surveys →
                    </Link>
                  </div>
                </div>
              ) : (
                <p>No recent surveys found.</p>
              )}

              <div className="mt-4">
                <ActionButton
                  to={`/orders/create?patientId=${patient.patient_id}`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Create New Order
                </ActionButton>
              </div>
            </Card>

            <Card title="Orders Summary" className="mt-6">
              {patient.orders && patient.orders.length > 0 ? (
                <div>
                  <div className="flex justify-between mb-3">
                    <div>
                      <FieldLabel>Total Orders</FieldLabel>
                      <FieldValue>{patient.orders.length}</FieldValue>
                    </div>
                    <div>
                      <FieldLabel>Latest Order</FieldLabel>
                      <FieldValue>
                        {formatDate(
                          patient.orders.sort(
                            (a, b) =>
                              new Date(b.order_date) - new Date(a.order_date)
                          )[0]?.order_date
                        )}
                      </FieldValue>
                    </div>
                  </div>
                  <div className="mt-4">
                    <Link
                      to="#"
                      onClick={() => setActiveTab("orders")}
                      className="text-primary-color font-medium hover:underline"
                    >
                      View all orders →
                    </Link>
                  </div>
                </div>
              ) : (
                <p>No orders on file.</p>
              )}
            </Card>
          </div>
        </PatientInfoGrid>
      )}

      {activeTab === "surveys" && (
        <div>
          <SectionHeader>
            <SurveysSectionTitle>Medicines Surveys</SurveysSectionTitle>
            <SectionCount>
              {patient.surveys?.length || 0} total surveys
            </SectionCount>
          </SectionHeader>

          {patient.surveys && patient.surveys.length > 0 ? (
            <SurveysContainer>
              {patient.surveys
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                .map((survey) => (
                  <SurveyCard key={survey.survey_id} survey={survey} />
                ))}
            </SurveysContainer>
          ) : (
            <Card>
              <EmptyStateContainer>
                <EmptyStateIcon
                  xmlns="http://www.w3.org/2000/svg"
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <path d="M14 2v6h6"></path>
                  <path d="M16 13H8"></path>
                  <path d="M16 17H8"></path>
                  <path d="M10 9H8"></path>
                </EmptyStateIcon>
                <EmptyStateTitle>No surveys on file</EmptyStateTitle>
                <EmptyStateMessage>
                  This patient hasn't completed any surveys yet.
                </EmptyStateMessage>
              </EmptyStateContainer>
            </Card>
          )}
        </div>
      )}

      {activeTab === "orders" && (
        <Card title="Order History">
          {patient.orders && patient.orders.length > 0 ? (
            <DataTable
              columns={[
                { header: "Order ID", accessor: "order_id" },
                {
                  header: "Date",
                  accessor: "order_date",
                  cell: (value) => formatDate(value)
                },
                {
                  header: "Pharmacy",
                  accessor: (row) => row.pharmacy?.pharmacy_name || "Unknown"
                },
                {
                  header: "Items",
                  accessor: (row) => row.order_items?.length || 0,
                  cell: (value) =>
                    `${value} ${value === 1 ? "medicine" : "medicines"}`
                },
                {
                  header: "Total",
                  accessor: "total_fee",
                  cell: (value) => {
                    if (!value && value !== 0) return "N/A";
                    try {
                      return `$${parseFloat(value).toFixed(2)}`;
                    } catch (e) {
                      console.error("Error formatting total_fee:", e);
                      return `$${value}`;
                    }
                  }
                },
                {
                  header: "Status",
                  accessor: "filled_by",
                  cell: (value) => (
                    <StatusBadge type={value ? "success" : "warning"}>
                      {value ? "Filled" : "Pending"}
                    </StatusBadge>
                  )
                },
                {
                  header: "Actions",
                  accessor: "order_id",
                  cell: (value) => (
                    <Link
                      to={`/orders/${value}`}
                      className="text-primary-color hover:underline"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.25rem"
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                      View
                    </Link>
                  )
                }
              ]}
              data={patient.orders.sort(
                (a, b) => new Date(b.order_date) - new Date(a.order_date)
              )}
              keyField="order_id"
            />
          ) : (
            <div>
              <p>No orders on file.</p>
              <div className="mt-4">
                <ActionButton
                  to={`/orders/create?patientId=${patient.patient_id}`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Create New Order
                </ActionButton>
              </div>
            </div>
          )}
        </Card>
      )}
    </PatientContainer>
  );
};

export default PatientDetailPage;






